import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Folder } from "lucide-react";
import ScanResultSheet from "@/components/ScanResultSheet";
import { MODE_MAP } from "@/lib/modes";

export default function CollectionDetailPage() {
  const { id } = useParams();
  const [col, setCol] = useState(null);
  const [scans, setScans] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cRes, sRes] = await Promise.all([
          api.get("/collections"),
          api.get("/scans"),
        ]);
        const found = cRes.data.find((c) => c.id === id);
        setCol(found || null);
        setScans(sRes.data.filter((s) => (s.collection_ids || []).includes(id)));
      } catch { /* ignore */ }
    })();
  }, [id]);

  const toggleFav = async (scan, favorite) => {
    try {
      const r = await api.patch(`/scans/${scan.id}/favorite`, { favorite });
      setScans((s) => s.map((x) => (x.id === scan.id ? r.data : x)));
      setSelected(r.data);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen pb-28 pt-6 px-4 max-w-md mx-auto">
      <Link to="/profile" className="inline-flex items-center gap-1 text-sm text-slate-400 mb-3">
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="flex items-center gap-3">
        <div className="text-4xl">{col?.emoji || "📁"}</div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl font-bold truncate" data-testid="collection-title">{col?.name || "Folder"}</h1>
          <p className="text-[11px] font-mono-data uppercase text-slate-500">{scans.length} scan{scans.length === 1 ? "" : "s"}</p>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {scans.length === 0 && (
          <div className="jx-glass rounded-2xl p-6 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
            <Folder className="text-slate-600" />
            <p>Nothing here yet. Save a scan to this folder from the result sheet.</p>
          </div>
        )}
        {scans.map((s) => {
          const m = MODE_MAP[s.mode] || MODE_MAP.jixxeo_lens;
          return (
            <button
              key={s.id}
              data-testid={`folder-scan-${s.id}`}
              onClick={() => setSelected(s)}
              className="w-full jx-glass rounded-2xl p-3 flex items-center gap-3 text-left active:scale-[0.99]"
            >
              {s.thumbnail && <img src={s.thumbnail} alt="" className="w-14 h-14 rounded-xl object-cover border border-white/10" />}
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold text-sm truncate">{s.title}</p>
                <p className="text-[11px] text-slate-400 truncate">{s.subtitle}</p>
                <p className="text-[10px] font-mono-data uppercase mt-0.5" style={{ color: m.color }}>{m.label} · {s.confidence.toFixed(0)}%</p>
              </div>
            </button>
          );
        })}
      </div>

      {selected && <ScanResultSheet result={selected} onClose={() => setSelected(null)} onToggleFavorite={toggleFav} />}
    </div>
  );
}
